import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Home, Briefcase, Users, ArrowRight } from 'lucide-react';

const API = '/api';

const ROLES = [
  {
    id: 'tenant', label: 'Tenant', icon: Home,
    desc: 'I am looking for a rental property to move into.',
    color: 'border-blue-200 hover:border-blue-500 hover:bg-blue-50',
    selectedColor: 'border-blue-600 bg-blue-50',
    iconBg: 'bg-blue-100 text-blue-700'
  },
  {
    id: 'owner', label: 'Property Owner', icon: Briefcase,
    desc: 'I own property and want to list it for rent.',
    color: 'border-slate-200 hover:border-green-400 hover:bg-green-50',
    selectedColor: 'border-green-500 bg-green-50',
    iconBg: 'bg-green-100 text-green-700'
  },
  {
    id: 'broker', label: 'Broker / Agent', icon: Users,
    desc: 'I am a real estate agent managing multiple properties.',
    color: 'border-slate-200 hover:border-amber-400 hover:bg-amber-50',
    selectedColor: 'border-amber-500 bg-amber-50',
    iconBg: 'bg-amber-100 text-amber-700'
  },
];

export default function RoleSelectPage() {
  const navigate = useNavigate();
  const { updateUser, user } = useAuth();
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selected) return toast.error('Please select your role');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ role: selected })
      });
      if (!res.ok) throw new Error('Failed to update role');
      const updated = await res.json();
      updateUser(updated);
      toast.success(`Welcome to Rent Mitra!`);
      // Redirect to mobile verification for new users
      navigate('/verify-mobile');
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Choose Your Role | Rent Mitra</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{fontFamily:'Outfit,sans-serif'}}>
            Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-slate-500">How will you be using Rent Mitra?</p>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_40px_rgb(0,0,0,0.08)] border border-slate-100 p-8">
          <div className="space-y-4 mb-8">
            {ROLES.map((role) => (
              <button
                key={role.id}
                onClick={() => setSelected(role.id)}
                className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                  selected === role.id ? role.selectedColor : role.color
                }`}
                data-testid={`role-${role.id}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${role.iconBg}`}>
                  <role.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{role.label}</div>
                  <div className="text-sm text-slate-500 mt-0.5">{role.desc}</div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 transition-all ${
                  selected === role.id ? 'border-blue-600 bg-blue-600' : 'border-slate-300'
                }`}>
                  {selected === role.id && (
                    <div className="w-full h-full rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!selected || loading}
            className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="confirm-role-button"
          >
            {loading ? 'Setting up...' : 'Continue'}
            {!loading && <ArrowRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
